import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnifiedSubscription } from '../../contexts/SubscriptionContext';
import { formatPercentage, safeSubtract, toSafeNumber } from '../../utils/number';
import { storeService, ConnectorType } from '../../services/store';
import {
  LayoutDashboard,
  Plug,
  Package,
  Upload,
  Images,
  Activity,
  CreditCard,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Menu,
  User,
  Users,
  Sparkles,
  Moon,
  Sun,
  FolderOpen,
  ClipboardCheck,
  History,
  Settings,
  Link2,
  Rss,
  Share2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface SidebarItem {
  id: string;
  label: string;
  href?: string;
  icon: React.ReactNode;
  badge?: number | string;
  requiresConnectorType?: ConnectorType; // undefined = always visible
  children?: SidebarItem[]; // For collapsible menus
  isCollapsible?: boolean;
}

const allSidebarItems: SidebarItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '/',
    icon: <LayoutDashboard className="h-5 w-5" />,
  },
  {
    id: 'connectors',
    label: 'Connectors',
    href: '/connectors',
    icon: <Plug className="h-5 w-5" />,
  },
  {
    id: 'magento-connector',
    label: 'Store',
    icon: <Package className="h-5 w-5" />,
    requiresConnectorType: 'magento',
    isCollapsible: true,
    children: [
      {
        id: 'magento-products',
        label: 'Products',
        href: '/products',
        icon: <Package className="h-4 w-4" />,
      },
      {
        id: 'jobs',
        label: 'Jobs',
        href: '/jobs',
        icon: <Activity className="h-4 w-4" />,
        badge: 'New',
      },
    ],
  },
  {
    id: 'upload-connector',
    label: 'Studio',
    icon: <FolderOpen className="h-5 w-5" />,
    requiresConnectorType: 'upload',
    isCollapsible: true,
    children: [
      {
        id: 'uploads',
        label: 'Upload',
        href: '/uploads',
        icon: <Upload className="h-4 w-4" />,
      },
      {
        id: 'assets',
        label: 'Assets',
        href: '/assets',
        icon: <Images className="h-4 w-4" />,
      },
      {
        id: 'review',
        label: 'Review',
        href: '/review',
        icon: <ClipboardCheck className="h-4 w-4" />,
      },
      {
        id: 'batch-history',
        label: 'Batch History',
        href: '/batch-history',
        icon: <History className="h-4 w-4" />,
      },
      {
        id: 'upload-products',
        label: 'Products',
        href: '/upload-products',
        icon: <Package className="h-4 w-4" />,
      },
    ],
  },
  {
    id: 'feedwatch',
    label: 'Feed Management',
    href: '/feedwatch',
    icon: <Rss className="h-5 w-5" />,
  },
  {
    id: 'social-posting',
    label: 'Social Posting',
    href: '/social-posting',
    icon: <Share2 className="h-5 w-5" />,
  },
  {
    id: 'team',
    label: 'Team',
    href: '/team',
    icon: <Users className="h-5 w-5" />,
  },
  {
    id: 'billing',
    label: 'Billing',
    href: '/billing',
    icon: <CreditCard className="h-5 w-5" />,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <Settings className="h-5 w-5" />,
    isCollapsible: true,
    children: [
      {
        id: 'integrations',
        label: 'Integrations',
        href: '/settings',
        icon: <Link2 className="h-4 w-4" />,
      },
    ],
  },
];

interface SidebarProps {
  currentPath?: string;
}

export const Sidebar: React.FC<SidebarProps> = () => {
  const { user, isAuthenticated } = useAuth();
  const { subscription, usageDetails, isLoading: isSubscriptionLoading, isAlertBannerVisible } = useUnifiedSubscription();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [activeConnectorTypes, setActiveConnectorTypes] = useState<Set<ConnectorType>>(new Set());
  const [connectorRefreshKey, setConnectorRefreshKey] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['upload-connector', 'magento-connector'])); // Start expanded

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev);
      if (next.has(menuId)) {
        next.delete(menuId);
      } else {
        next.add(menuId);
      }
      return next;
    });
  };

  // Fetch active connector types
  const fetchConnectorTypes = async () => {
    try {
      const stores = await storeService.getStores();
      const types = new Set<ConnectorType>();
      stores.forEach(store => {
        if (store.connector_type) {
          types.add(store.connector_type);
        }
      });
      setActiveConnectorTypes(types);
    } catch (err) {
      // Silently fail - show all items if we can't fetch stores
      console.error('Failed to fetch connector types:', err);
    }
  };

  // Listen for connector changes (custom event)
  useEffect(() => {
    const handleConnectorsChanged = () => {
      setConnectorRefreshKey(prev => prev + 1);
    };

    window.addEventListener('connectors-changed', handleConnectorsChanged);
    return () => window.removeEventListener('connectors-changed', handleConnectorsChanged);
  }, []);

  // Fetch connector types on mount and when refresh key changes
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchConnectorTypes();
  }, [isAuthenticated, connectorRefreshKey]);

  // Filter sidebar items based on active connector types
  const sidebarItems = useMemo(() => {
    return allSidebarItems.filter(item => {
      // Always show items without a connector type requirement
      if (!item.requiresConnectorType) return true;
      // Show items if their required connector type is active
      return activeConnectorTypes.has(item.requiresConnectorType);
    });
  }, [activeConnectorTypes]);

  // Handle keyboard shortcut for sidebar toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setIsCollapsed(!isCollapsed);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isCollapsed]);

  // Persist sidebar state in localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Initialize theme
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDark(stored === 'dark');
      document.documentElement.classList.toggle('dark', stored === 'dark');
    } else {
      setIsDark(true);
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.classList.toggle('dark', newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const SidebarItem: React.FC<{ item: typeof sidebarItems[0]; isChild?: boolean }> = ({ item, isChild = false }) => {
    // For products, uploads, assets, and review, highlight for any related routes
    const isActive = item.id === 'magento-products'
      ? location.pathname.startsWith('/products')
      : item.id === 'uploads'
      ? location.pathname.startsWith('/uploads')
      : item.id === 'assets'
      ? location.pathname.startsWith('/assets')
      : item.id === 'review'
      ? location.pathname.startsWith('/review')
      : item.id === 'batch-history'
      ? location.pathname.startsWith('/batch-history')
      : item.id === 'upload-products'
      ? location.pathname.startsWith('/upload-products')
      : item.id === 'feedwatch'
      ? location.pathname.startsWith('/feedwatch')
      : item.id === 'social-posting'
      ? location.pathname.startsWith('/social-posting')
      : item.id === 'integrations'
      ? location.pathname.startsWith('/settings')
      : item.href ? location.pathname === item.href : false;

    // Check if any child is active (for collapsible parent highlight)
    const hasActiveChild = item.children?.some(child => {
      if (child.id === 'uploads') return location.pathname.startsWith('/uploads');
      if (child.id === 'assets') return location.pathname.startsWith('/assets');
      if (child.id === 'review') return location.pathname.startsWith('/review');
      if (child.id === 'batch-history') return location.pathname.startsWith('/batch-history');
      if (child.id === 'upload-products') return location.pathname.startsWith('/upload-products');
      if (child.id === 'feedwatch') return location.pathname.startsWith('/feedwatch');
      if (child.id === 'magento-products') return location.pathname.startsWith('/products');
      if (child.id === 'jobs') return location.pathname.startsWith('/jobs');
      if (child.id === 'integrations') return location.pathname.startsWith('/settings');
      return child.href ? location.pathname === child.href : false;
    });

    const isExpanded = expandedMenus.has(item.id);

    // Handle collapsible menu item
    if (item.isCollapsible && item.children) {
      const menuButton = (
        <button
          onClick={() => toggleMenu(item.id)}
          className={`
            w-full group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200
            ${isCollapsed ? 'justify-center px-2' : ''}
            ${hasActiveChild
              ? 'bg-accent/50 text-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }
          `}
        >
          <span className={`shrink-0 transition-transform group-hover:scale-110 ${
            hasActiveChild ? 'text-primary' : 'text-current'
          }`}>
            {item.icon}
          </span>
          {!isCollapsed && (
            <>
              <span className="truncate flex-1 text-left">{item.label}</span>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </>
          )}
        </button>
      );

      if (isCollapsed) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {menuButton}
              </TooltipTrigger>
              <TooltipContent side="right" className="p-0">
                <div className="py-2 min-w-[160px]">
                  <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase">
                    {item.label}
                  </div>
                  {item.children.map(child => (
                    <Link
                      key={child.id}
                      to={child.href || '#'}
                      className={`
                        flex items-center gap-2 px-3 py-2 text-sm transition-colors
                        ${location.pathname.startsWith(child.href || '')
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                        }
                      `}
                    >
                      {child.icon}
                      <span>{child.label}</span>
                    </Link>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      return (
        <div>
          {menuButton}
          {isExpanded && (
            <div className="mt-1 ml-4 space-y-1 border-l border-border pl-3">
              {item.children.map(child => (
                <SidebarItem key={child.id} item={child} isChild />
              ))}
            </div>
          )}
        </div>
      );
    }

    // Regular link item
    const linkContent = (
      <Link
        to={item.href || '#'}
        className={`
          group flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200
          ${isChild ? 'px-2 py-2' : 'px-3 py-2.5'}
          ${isCollapsed ? 'justify-center px-2' : ''}
          ${
            isActive
              ? 'bg-gradient-primary text-white shadow-lg shadow-primary/25'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-md'
          }
        `}
      >
        <span className={`shrink-0 transition-transform group-hover:scale-110 ${
          isActive ? 'text-white' : 'text-current'
        }`}>
          {item.icon}
        </span>
        {!isCollapsed && (
          <>
            <span className="truncate">{item.label}</span>
            {item.badge && (
              <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                isActive
                  ? 'bg-primary/20 text-primary-foreground'
                  : 'bg-primary text-primary-foreground'
              }`}>
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    );

    if (isCollapsed && !isChild) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {linkContent}
            </TooltipTrigger>
            <TooltipContent side="right" className="flex items-center gap-2">
              <span>{item.label}</span>
              {item.badge && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                  {item.badge}
                </span>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return linkContent;
  };

  return (
    <aside
      className={`
        fixed left-0 z-40 bg-card border-r border-border transition-all duration-300 ease-in-out flex flex-col
        ${isAlertBannerVisible ? 'top-14 h-[calc(100vh-3.5rem)]' : 'top-0 h-screen'}
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Logo Section */}
      <div className={`flex-shrink-0 border-b border-border ${isCollapsed ? 'p-3' : 'p-2'}`}>
        {!isCollapsed ? (
          <Link to="/" className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-accent/50 transition-all duration-200 group">
            <div className="w-5 h-5 bg-gradient-primary rounded-lg flex items-center justify-center transition-transform group-hover:scale-110">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="text-lg font-bold bg-gradient-primary bg-clip-text text-transparent">
              TinyScale Studio
            </span>
          </Link>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/" className="flex justify-center rounded-xl px-2 py-2.5 hover:bg-accent/50 transition-all duration-200 group">
                  <div className="w-5 h-5 bg-gradient-primary rounded-lg flex items-center justify-center transition-transform group-hover:scale-110">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">
                <span className="font-semibold">TinyScale Studio</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Navigation - Scrollable */}
      <nav className={`flex-1 overflow-y-auto p-4 space-y-2 ${isCollapsed ? 'px-2' : ''}`}>
        {sidebarItems.map((item) => (
          <SidebarItem key={item.id} item={item} />
        ))}
      </nav>

      {/* Fixed Bottom Section */}
      <div className="flex-shrink-0 border-t border-border">
        {/* User Section - Always visible */}
        {user && (
          <div className={`p-4 border-b border-border ${isCollapsed ? 'px-2' : ''}`}>
            {!isCollapsed ? (
              <div className="space-y-3">
                {/* User Info */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                  <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {user.email?.split('@')[0] || 'User'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </div>
                  </div>
                </div>

                {/* Subscription Info */}
                {isSubscriptionLoading ? (
                  <div className="p-3 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-4 bg-muted rounded animate-pulse w-16"></div>
                      <div className="h-3 bg-muted rounded animate-pulse w-8"></div>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-muted-foreground/20 h-2 rounded-full animate-pulse w-1/2"></div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      <div className="h-3 bg-muted rounded animate-pulse w-20"></div>
                    </div>
                  </div>
                ) : subscription ? (
                  <div className="p-3 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">
                        {subscription.plan_name}
                      </span>
                      <span className="text-xs font-medium text-primary">
                        {usageDetails?.credits?.percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-gradient-primary h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${Math.min(usageDetails?.credits?.percentage || 0, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {safeSubtract(100, toSafeNumber(usageDetails?.credits?.percentage)) > 0 
                        ? `${formatPercentage(safeSubtract(100, toSafeNumber(usageDetails?.credits?.percentage)), 1)} remaining`
                        : 'Limit reached'
                      }
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center mx-auto cursor-pointer">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <div className="space-y-1">
                      <div className="font-medium">{user.email?.split('@')[0] || 'User'}</div>
                      <div className="text-xs text-muted-foreground">{user.email}</div>
                      {isSubscriptionLoading ? (
                        <div className="text-xs">
                          <div className="font-medium">Loading...</div>
                        </div>
                      ) : subscription ? (
                        <div className="text-xs">
                          <div className="font-medium">{subscription.plan_name}</div>
                          <div>{usageDetails?.credits?.percentage?.toFixed(1) || 0}% used</div>
                        </div>
                      ) : null}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        {/* Controls Section - Always visible */}
        <div className="bg-muted/20">
          <div className="p-3 space-y-3">
            {/* Theme Toggle */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleTheme}
                className={`gap-2 hover:bg-accent transition-all duration-200 text-xs font-medium ${
                  isCollapsed ? 'h-8 w-8 p-0' : 'h-9 px-4'
                }`}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              >
                {isCollapsed ? (
                  isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />
                ) : (
                  <>
                    {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                    {isDark ? 'Light Mode' : 'Dark Mode'}
                  </>
                )}
              </Button>
            </div>
            
            {/* Keyboard shortcut hint - Always show */}
            <div className="text-center">
              <span className="text-xs text-muted-foreground">
                Press <kbd className="px-2 py-1 text-xs font-mono bg-muted border border-border rounded shadow-sm">⌘B</kbd> to toggle
              </span>
            </div>
            
            {/* Collapse button - Always show */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={`gap-2 hover:bg-accent transition-all duration-200 text-xs font-medium ${
                  isCollapsed ? 'h-8 w-8 p-0' : 'h-9 px-4'
                }`}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <Menu className="h-4 w-4" />
                ) : (
                  <>
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Collapse
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};